<template>
	<component
		:is="tag"
		v-bind="$attrs"
		:class="trackingClass"
		:data-track-event="eventName"
		:data-track-category="category"
		@click="handleClick"
	>
		<slot />
	</component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useTracking } from '@/services/tracking';
import { TrackingEventType } from '@monorepo/types';

interface Props {
	// Event configuration
	event: string;
	category?:
		| 'cta'
		| 'navigation'
		| 'form'
		| 'engagement'
		| 'monetization'
		| 'performance'
		| 'experiment';

	// Event data
	eventData?: Record<string, any>;

	// CTA specific props
	ctaType?: 'primary' | 'secondary' | 'tertiary';
	ctaLocation?: string;
	pageSection?: string;

	// Form specific props
	formName?: string;
	fieldName?: string;

	// Engagement specific props
	contentType?: string;
	contentId?: string;
	pageName?: string;

	// Monetization specific props
	productType?: string;
	price?: number;

	// Experiment specific props
	experimentName?: string;
	variantName?: string;

	// Component props
	tag?: string;
	disabled?: boolean;

	// Tracking options
	trackOnMount?: boolean;
	trackOnHover?: boolean;
	debounceMs?: number;
}

const props = withDefaults(defineProps<Props>(), {
	category: 'engagement',
	tag: 'div',
	disabled: false,
	trackOnMount: false,
	trackOnHover: false,
	debounceMs: 0,
});

const emit = defineEmits<{
	click: [event: MouseEvent];
	tracked: [eventData: any];
}>();

// Lazy tracking service getter
let _tracking: ReturnType<typeof useTracking> | null = null;
const getTracking = () => {
	if (!_tracking) {
		_tracking = useTracking();
	}
	return _tracking;
};

// Computed properties
const eventName = computed(() => props.event);
const trackingClass = computed(() => {
	const classes = ['track-click'];
	if (props.category) classes.push(`track-${props.category}`);
	if (props.disabled) classes.push('track-disabled');
	return classes.join(' ');
});

// Debounce helper
let debounceTimer: number | null = null;

const debounce = (func: Function, delay: number) => {
	return (...args: any[]) => {
		if (debounceTimer) clearTimeout(debounceTimer);
		debounceTimer = window.setTimeout(() => func.apply(null, args), delay);
	};
};

// Main tracking function
const trackEvent = (clickEvent?: MouseEvent) => {
	if (props.disabled) return;

	const elementText = (clickEvent?.target as HTMLElement)?.textContent?.trim() || '';
	const pageSection = props.pageSection || 'unknown';

	// Track based on category
	const tracking = getTracking();

	switch (props.category) {
		case 'cta':
			tracking.trackCTAClick({
				eventType: TrackingEventType.CTA_CLICKED,
				ctaType: props.ctaType || 'primary',
				ctaText: elementText,
				ctaLocation: props.ctaLocation || 'unknown',
				metadata: {
					pageSection,
					...props.eventData,
				},
			});
			break;

		case 'form':
			// Use NAVIGATION_CLICK for form interactions since FORM_INTERACTION is not in EngagementEvent
			tracking.trackEngagementEvent({
				eventType: TrackingEventType.NAVIGATION_CLICK,
				pageUrl: window.location.href,
				metadata: {
					formName: props.formName || 'unknown',
					fieldName: props.fieldName || 'unknown',
					interactionType: 'change',
					eventCategory: 'form_interaction',
					...props.eventData,
				},
			});
			break;

		case 'navigation':
			tracking.trackEngagementEvent({
				eventType: TrackingEventType.NAVIGATION_CLICK,
				pageUrl: window.location.href,
				metadata: {
					contentId: props.pageName || 'unknown',
					pageSection,
					...props.eventData,
				},
			});
			break;

		case 'monetization':
			tracking.trackMonetizationEvent({
				eventType: TrackingEventType.PAYMENT_INITIATED,
				amount: props.price || 0,
				metadata: {
					productType: props.productType,
					...props.eventData,
				},
			});
			break;

		case 'performance':
			tracking.trackPerformanceEvent({
				eventType: TrackingEventType.PAGE_PERFORMANCE,
				loadTime: 0,
				metadata: {
					pageName: props.pageName || 'unknown',
					...props.eventData,
				},
			});
			break;

		case 'experiment':
			tracking.trackExperimentEvent({
				eventType: TrackingEventType.VARIANT_ASSIGNED,
				experimentId: props.experimentName || 'unknown',
				variantId: props.variantName || 'unknown',
				metadata: {
					...props.eventData,
				},
			});
			break;

		case 'engagement':
		default:
			tracking.trackEngagementEvent({
				eventType: TrackingEventType.PAGE_ENGAGEMENT,
				pageUrl: window.location.href,
				metadata: {
					contentId: props.contentId || props.pageName || 'unknown',
					contentType: props.contentType,
					...props.eventData,
				},
			});
			break;
	}

	// Emit tracked event
	emit('tracked', {
		event: eventName.value,
		category: props.category,
		timestamp: Date.now(),
		...props.eventData,
	});
};

// Debounced tracking if specified
const debouncedTrack = props.debounceMs > 0 ? debounce(trackEvent, props.debounceMs) : trackEvent;

// Click handler
const handleClick = (event: MouseEvent) => {
	// Track the click
	debouncedTrack(event);

	// Emit the original click event
	emit('click', event);
};

// Optional hover tracking
if (props.trackOnHover) {
	// Add hover event listener if needed
	// This would need to be implemented in the template
}

// Optional mount tracking
if (props.trackOnMount) {
	const tracking = getTracking();
	tracking.trackEngagementEvent({
		eventType: TrackingEventType.PAGE_VIEW,
		pageUrl: window.location.href,
		metadata: {
			contentId: props.pageName || 'unknown',
			...props.eventData,
		},
	});
}
</script>

<script lang="ts">
export default {
	name: 'TrackClick',
	inheritAttrs: false,
};
</script>

<style scoped>
.track-click {
	/* Base tracking styles */
}

.track-cta {
	/* CTA specific styles if needed */
}

.track-disabled {
	/* Disabled state styles */
	pointer-events: none;
	opacity: 0.6;
}
</style>
